import {
	createContext,
	useContext,
	useState,
	ReactNode,
	useEffect,
	useCallback,
} from "react";
import {
	login as apiLogin,
	register as apiRegister,
	refreshToken as apiRefreshToken,
} from "@/api/auth";
import { AxiosError } from "axios";

interface AuthContextType {
	isAuthenticated: boolean;
	login: (email: string, password: string) => Promise<void>;
	register: (email: string, password: string) => Promise<void>;
	logout: () => void;
	refreshSession: () => Promise<boolean>;
}

const TOKEN_REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes
const TOKEN_EXPIRY_BUFFER = 5 * 60 * 1000; // 5 minutes buffer before expiry

const AuthContext = createContext<AuthContextType | null>(null);

function AuthProviderContent({ children }: { children: ReactNode }) {
	const [isAuthenticated, setIsAuthenticated] = useState(() => {
		const token = localStorage.getItem("accessToken");
		const tokenExpiry = localStorage.getItem("tokenExpiry");
		if (!token || !tokenExpiry) return false;

		// Check if token is expired or close to expiry
		const expiryTime = parseInt(tokenExpiry, 10);
		const now = Date.now();
		return expiryTime > now + TOKEN_EXPIRY_BUFFER;
	});

	// Function to handle logout
	const handleLogout = useCallback(() => {
		localStorage.removeItem("refreshToken");
		localStorage.removeItem("accessToken");
		localStorage.removeItem("tokenExpiry");
		localStorage.removeItem("campaign_draft");
		localStorage.removeItem("campaign_draft_id");
		setIsAuthenticated(false);
		window.location.href = "/login";
	}, []);

	// Function to refresh the token
	const refreshSession = useCallback(async (): Promise<boolean> => {
		try {
			const refreshTokenValue = localStorage.getItem("refreshToken");
			if (!refreshTokenValue) {
				throw new Error("No refresh token available");
			}

			const response = await apiRefreshToken(refreshTokenValue);
			if (response?.accessToken && response?.refreshToken) {
				localStorage.setItem("accessToken", response.accessToken);
				localStorage.setItem("refreshToken", response.refreshToken);
				// Set token expiry to 15 minutes from now
				const expiryTime = Date.now() + 15 * 60 * 1000;
				localStorage.setItem("tokenExpiry", expiryTime.toString());
				setIsAuthenticated(true);
				return true;
			}
			return false;
		} catch (error) {
			console.error("Token refresh failed:", error);
			handleLogout();
			return false;
		}
	}, [handleLogout]);

	// Set up automatic token refresh
	useEffect(() => {
		if (isAuthenticated) {
			// Initial check for token validity
			const checkTokenValidity = async () => {
				const tokenExpiry = localStorage.getItem("tokenExpiry");
				if (!tokenExpiry) {
					handleLogout();
					return;
				}

				const expiryTime = parseInt(tokenExpiry, 10);
				const now = Date.now();

				if (expiryTime <= now + TOKEN_EXPIRY_BUFFER) {
					const success = await refreshSession();
					if (!success) {
						handleLogout();
					}
				}
			};

			// Check immediately and set up interval
			checkTokenValidity();
			const tokenRefreshInterval = setInterval(
				checkTokenValidity,
				TOKEN_REFRESH_INTERVAL
			);

			return () => clearInterval(tokenRefreshInterval);
		}
	}, [isAuthenticated, refreshSession, handleLogout]);

	const login = useCallback(
		async (email: string, password: string) => {
			try {
				const response = await apiLogin(email, password);
				if (response?.refreshToken && response?.accessToken) {
					localStorage.setItem("refreshToken", response.refreshToken);
					localStorage.setItem("accessToken", response.accessToken);
					// Set token expiry to 15 minutes from now
					const expiryTime = Date.now() + 15 * 60 * 1000;
					localStorage.setItem("tokenExpiry", expiryTime.toString());
					setIsAuthenticated(true);
				} else {
					console.error("Login failed: Invalid response format", response);
					throw new Error("Login failed: Invalid server response");
				}
			} catch (error) {
				console.error("Login error:", error);
				handleLogout();
				if (error instanceof AxiosError) {
					throw new Error(
						error.response?.data?.error || error.message || "Login failed"
					);
				}
				throw error;
			}
		},
		[handleLogout]
	);

	const register = useCallback(
		async (email: string, password: string) => {
			try {
				await apiRegister(email, password);
				await login(email, password);
			} catch (error) {
				console.error("Registration error:", error);
				handleLogout();
				if (error instanceof AxiosError) {
					throw new Error(
						error.response?.data?.error ||
							error.message ||
							"Registration failed"
					);
				}
				throw error;
			}
		},
		[handleLogout, login]
	);

	const contextValue = {
		isAuthenticated,
		login,
		register,
		logout: handleLogout,
		refreshSession,
	};

	return (
		<AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
	);
}

export function AuthProvider({ children }: { children: ReactNode }) {
	return <AuthProviderContent>{children}</AuthProviderContent>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
}
